<template>
  <div class="action" :class="{ mobile: isMobile }">
    <h6 v-if="!(isEditingAlert && isMobile)">
      <span v-if="isMobile" @click="backToIndexPage">
        <fa icon="chevron-left" class="icon"></fa>
      </span>
      Notification
    </h6>
    <p>
      Nominate and notify recipients when the system triggers the alert.
    </p>
    <span v-if="isMobile" class="separate-line"></span>

    <template v-if="!isMobile">
      <!-- Add recipient block -->
      <div class="action-input-container">
        <div class="channel">
          <div class="title">Channel</div>
          <Selector
            width="100%"
            :optionList="channelTypeList"
            :value="actionObj.channelType"
            @onValueChange="setChannelType"
          ></Selector>
        </div>
        <div class="recipient">
          <template v-if="actionObj.channelType === ACTION_CHANNEL_TYPE.EMAIL">
            <div class="title">Recipient</div>
            <Selector
              width="100%"
              :optionList="recipientTypeList"
              :value="actionObj.recipientType"
              @onValueChange="setRecipientType"
            ></Selector>
          </template>
          <template v-if="actionObj.channelType === ACTION_CHANNEL_TYPE.SMS">
            <div class="title ">Phone number (country code required)</div>
            <div class="mobile-input-container">
              <input
                type="number"
                class="form-control country-code"
                placeholder="CC"
                v-model.number="countryCode"
                @input="handleCountryCodeInput"
                @keypress="preventNonNumericalInput"
              />
              <input
                type="number"
                class="form-control mobile-number"
                placeholder="Mobile number"
                v-model.number="mobileNumber"
                @input="handleMobileNumberInput"
                @keypress="preventNonNumericalInput"
              />
            </div>
          </template>
          <template v-if="actionObj.channelType === ACTION_CHANNEL_TYPE.SLACK">
            <div class="title ">Slack Channel ID</div>
            <div class="slack-input-container">
              <input
                type="text"
                class="form-control"
                placeholder="Enter Slack Channel ID here"
                v-model.trim="slackId"
              />
            </div>
          </template>
        </div>
        <div class="add">
          <button
            type="button"
            class="btn btn-primary"
            :class="{ 'not-visible': isCustomRecipientInputShown }"
            :disabled="isAddButtonDisabled"
            @click="addToList($event)"
          >
            Add to list
          </button>
        </div>
      </div>

      <!-- Add email recipient, either member contact email or custom email -->
      <div
        class="custom-recipient-input-container"
        v-if="isCustomRecipientInputShown"
      >
        <div class="custom-recipient-input">
          <template
            v-if="
              actionObj.recipientType === EMAIL_RECIPIENT_TYPE.MEMBER_CONTACT
            "
          >
            <Selector
              width="100%"
              :optionList="memberContactList"
              :value="memberContact"
              @onValueChange="setMemberContact"
            ></Selector>
          </template>
          <template
            v-if="
              actionObj.recipientType === EMAIL_RECIPIENT_TYPE.PROVIDED_ADDRESS
            "
          >
            <input
              type="email"
              class="form-control"
              placeholder="Enter email address"
              v-model="emailAddress"
              ref="email"
              maxlength="254"
            />
          </template>
        </div>
        <div class="add">
          <button
            type="button"
            class="btn btn-primary"
            @click.prevent="addToList"
            :disabled="isAddButtonDisabled"
          >
            Add to list
          </button>
        </div>
      </div>

      <!-- Profile help link for myself email -->
      <div
        v-if="
          actionObj.channelType === ACTION_CHANNEL_TYPE.EMAIL &&
            actionObj.recipientType === EMAIL_RECIPIENT_TYPE.MYSELF
        "
        class="help-link"
      >
        Your email address can be updated at
        <a :href="personalProfileURL" target="_blank"
          >your personal profile <fa icon="external-link-alt"></fa></a
        >.
      </div>

      <!-- Warning of invalid email address -->
      <div
        v-if="
          actionObj.channelType === ACTION_CHANNEL_TYPE.EMAIL &&
            actionObj.recipientType === EMAIL_RECIPIENT_TYPE.PROVIDED_ADDRESS &&
            isInvalidEmailAddress
        "
        class="warning-text"
      >
        {{ invalidEmailWarningText }}
      </div>

      <!-- Add Slack recipient -->
      <div
        v-if="actionObj.channelType === ACTION_CHANNEL_TYPE.SLACK"
        class="help-link"
      >
        <a :href="SlackHelpURL" target="_blank"
          >Slack setup help <fa icon="external-link-alt"></fa
        ></a>
      </div>

      <!-- Warning of duplicated recipient -->
      <div v-if="isDuplicatedRecipient" class="warning-text">
        This recipient has already been added.
      </div>

      <!-- Warning of input being required -->
      <div
        v-if="
          (isInvalidInputWarningShown || isEditingAlert) &&
            !this.actionObj.recipientList.length
        "
        class="warning-text mb-n2"
      >
        At least one notification recipient must be defined.
      </div>

      <!-- Recipient List -->
      <div class="recipient-list-container">
        <div class="title-row">
          <div class="title channel">
            Channel
          </div>
          <div class="title recipient">
            Recipient
          </div>
          <div class="empty-block"></div>
        </div>
        <div v-if="recipientListWithRecipientName.length === 0" class="list">
          <span class="empty-list">
            (No recipient added)
          </span>
        </div>
        <div v-else class="list">
          <ul>
            <li
              v-for="(item, index) in recipientListWithRecipientName"
              :key="item.recipientValue + index"
            >
              <span class="channel">{{ parseChannelType(item.channel) }}</span>
              <span
                class="recipient truncated"
                :class="{ italic: item.recipientValue.indexOf('(myself)') }"
                >{{ item.recipientValue }}</span
              >
              <div
                class="icon-container"
                @click="removeRecipient(index)"
                v-tooltip.top="{
                  content: tooltipText
                }"
              >
                <fa icon="minus-circle" class="icon"></fa>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <!-- Mobile design -->
    <template v-else>
      <div
        v-if="!recipientListWithRecipientName.length && !isAddingActionShown"
        class="empty-recipient"
      >
        <p class="title">No notifications have been listed yet.</p>
        <div class="add-action">
          <button
            type="button"
            class="btn btn-primary"
            @click="isAddingActionShown = true"
          >
            Add notification
          </button>
        </div>
        <!-- Warning of input being required -->
        <div
          v-if="
            (isInvalidInputWarningShown || isEditingAlert) &&
              !this.actionObj.recipientList.length
          "
          class="warning-text"
        >
          At least one notification recipient must be defined.
        </div>
      </div>
      <div v-if="isAddingActionShown" class="add-recipient-container">
        <div class="title">Create new notification</div>

        <!-- Add recipient block -->
        <div class="action-input-container">
          <div class="channel">
            <div class="title">Channel</div>
            <Selector
              width="100%"
              :optionList="channelTypeList"
              :value="actionObj.channelType"
              @onValueChange="setChannelType"
            ></Selector>
          </div>
          <div class="recipient">
            <template
              v-if="actionObj.channelType === ACTION_CHANNEL_TYPE.EMAIL"
            >
              <div class="title">Recipient</div>
              <Selector
                width="100%"
                :optionList="recipientTypeList"
                :value="actionObj.recipientType"
                @onValueChange="setRecipientType"
              ></Selector>
            </template>

            <!-- Add email recipient, either member contact email or custom email -->
            <div
              v-if="isCustomRecipientInputShown"
              class="custom-recipient-input"
            >
              <template
                v-if="
                  actionObj.recipientType ===
                    EMAIL_RECIPIENT_TYPE.MEMBER_CONTACT
                "
              >
                <div class="title">Member contact</div>
                <Selector
                  width="100%"
                  :optionList="memberContactList"
                  :value="memberContact"
                  @onValueChange="setMemberContact"
                ></Selector>
              </template>
              <template
                v-if="
                  actionObj.recipientType ===
                    EMAIL_RECIPIENT_TYPE.PROVIDED_ADDRESS
                "
              >
                <div class="title">Email address</div>
                <input
                  type="email"
                  class="form-control"
                  placeholder="Enter email address"
                  v-model="emailAddress"
                  ref="email"
                  maxlength="254"
                />
              </template>
            </div>

            <template v-if="actionObj.channelType === ACTION_CHANNEL_TYPE.SMS">
              <div class="title ">Phone number (country code required)</div>
              <div class="mobile-input-container">
                <input
                  type="number"
                  class="form-control country-code"
                  placeholder="CC"
                  v-model.number="countryCode"
                  @input="handleCountryCodeInput"
                  @keypress="preventNonNumericalInput"
                />
                <input
                  type="number"
                  class="form-control mobile-number"
                  placeholder="Mobile number"
                  v-model.number="mobileNumber"
                  @input="handleMobileNumberInput"
                  @keypress="preventNonNumericalInput"
                />
              </div>
            </template>
            <template
              v-if="actionObj.channelType === ACTION_CHANNEL_TYPE.SLACK"
            >
              <div class="title ">Slack Channel ID</div>
              <div class="slack-input-container">
                <input
                  type="text"
                  class="form-control"
                  placeholder="Enter Slack Channel ID here"
                  v-model.trim="slackId"
                />
              </div>
            </template>
          </div>

          <!-- Profile help link for myself email -->
          <div
            v-if="
              actionObj.channelType === ACTION_CHANNEL_TYPE.EMAIL &&
                actionObj.recipientType === EMAIL_RECIPIENT_TYPE.MYSELF
            "
            class="help-link"
          >
            Your email address can be updated at
            <a :href="personalProfileURL" target="_blank"
              >your personal profile <fa icon="external-link-alt"></fa>
            </a>
            .
          </div>

          <!-- Warning of invalid email address -->
          <div
            v-if="
              actionObj.channelType === ACTION_CHANNEL_TYPE.EMAIL &&
                actionObj.recipientType ===
                  EMAIL_RECIPIENT_TYPE.PROVIDED_ADDRESS &&
                isInvalidEmailAddress
            "
            class="warning-text"
          >
            {{ invalidEmailWarningText }}
          </div>

          <!-- Warning of duplicated recipient -->
          <div v-if="isDuplicatedRecipient" class="warning-text">
            This recipient has already been added.
          </div>

          <!-- Add Slack recipient -->
          <div
            v-if="actionObj.channelType === ACTION_CHANNEL_TYPE.SLACK"
            class="help-link"
          >
            <a :href="SlackHelpURL" target="_blank">
              Slack setup help
              <fa icon="external-link-alt"></fa>
            </a>
          </div>

          <div class="add">
            <button
              type="button"
              class="btn btn-outline-primary"
              @click="isAddingActionShown = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isAddButtonDisabled"
              @click="addToList($event)"
            >
              Add to list
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="recipientListWithRecipientName.length"
        class="recipient-list-container"
      >
        <div class="action-title-container">
          <div class="title">Notifications</div>
          <div v-if="!isAddingActionShown" class="add-action">
            <button
              type="button"
              class="btn btn-primary"
              @click="isAddingActionShown = true"
            >
              Add notification
            </button>
          </div>
        </div>
        <ul>
          <li
            v-for="(item, index) in recipientListWithRecipientName"
            :key="item.recipientValue + index"
            class="recipient-list"
          >
            <div class="card">
              <div class="card-header">
                <div class="card-header__text">
                  {{ parseChannelType(item.channel) }}
                </div>
                <button
                  type="button"
                  class="card-header__icon close"
                  aria-label="delete"
                  @click="removeRecipient(index)"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="card-body">
                <p class="card-text">
                  {{ item.recipientValue }}
                </p>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </template>

    <div class="form-check sending-notification">
      <input
        class="form-check-input"
        type="checkbox"
        v-model="actionObj.isSendingAlertResolvedNotification"
        id="is-sending-alert-resolved-notification"
      />
      <label
        class="form-check-label"
        for="is-sending-alert-resolved-notification"
      >
        Send notification when alert has been resolved.
      </label>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import {
  ACTION_CHANNEL_TYPE,
  EMAIL_RECIPIENT_TYPE,
  RECIPIENT_TYPE_IN_API
} from '../../../constants/alertsConstant';
import Selector from '../../ui/Selector.vue';
import {
  validateEmailAddressByLength,
  replaceChartInString
} from '../../../utils/common';

export default {
  name: 'Action',
  components: { Selector },
  props: ['actionObj', 'isInvalidInputWarningShown'],
  data() {
    return {
      ACTION_CHANNEL_TYPE: ACTION_CHANNEL_TYPE,
      EMAIL_RECIPIENT_TYPE: EMAIL_RECIPIENT_TYPE,
      tooltipText: 'Remove recipient',
      memberContact: '',
      emailAddress: '',
      countryCode: null,
      mobileNumber: null,
      slackId: '',
      isDuplicatedRecipient: false,
      isInvalidEmailAddress: false,
      invalidEmailWarningText: '',
      channelTypeList: [
        {
          name: 'Email',
          value: ACTION_CHANNEL_TYPE.EMAIL
        },
        {
          name: 'SMS',
          value: ACTION_CHANNEL_TYPE.SMS
        },
        {
          name: 'Slack',
          value: ACTION_CHANNEL_TYPE.SLACK
        }
      ],
      recipientTypeList: [
        {
          name: 'Myself',
          value: EMAIL_RECIPIENT_TYPE.MYSELF
        },
        {
          name: 'Member contact',
          value: EMAIL_RECIPIENT_TYPE.MEMBER_CONTACT
        },
        {
          name: 'Provided address',
          value: EMAIL_RECIPIENT_TYPE.PROVIDED_ADDRESS
        }
      ],
      isAddingActionShown: false
    };
  },

  computed: {
    ...mapState('dataStore', ['selectedAccount']),
    ...mapState('alertsSuspiciousTrafficStore', ['isEditingAlert']),
    ...mapGetters(['isMobile']),
    ...mapGetters('authStore', ['oidcUser']),
    ...mapGetters('alertsSuspiciousTrafficStore', [
      'memberContactsOfSelectedAccount'
    ]),

    personalProfileURL() {
      return `https://${process.env.VUE_APP_ROUTING_MANAGEMENT_DASHBOARD}/${
        this.selectedAccount.account
      }/resources/index.html`;
    },
    SlackHelpURL() {
      return process.env.VUE_APP_SLACK_HELP_URL;
    },
    memberContactList() {
      const formattedList = this.memberContactsOfSelectedAccount.map(item => ({
        value: item.uid,
        ...item
      }));

      return [
        { name: 'Select member contact...', value: '' },
        ...formattedList
      ];
    },

    recipientListWithRecipientName() {
      const memberContactList = this.memberContactsOfSelectedAccount.map(
        item => ({
          value: item.uid,
          ...item
        })
      );

      const userName =
        this.oidcUser.nickname ||
        this.oidcUser.name ||
        this.oidcUser.email ||
        '';
      memberContactList.unshift({
        value: this.oidcUser.userid,
        name: `${userName} (myself)`
      });

      const recipientList = this.actionObj.recipientList.map(item => {
        if (item.recipientType === RECIPIENT_TYPE_IN_API.NON_APNIC_USER) {
          return item;
        } else {
          const memberContact = memberContactList.find(
            memberContact => memberContact.value === item.recipientValue
          );
          return {
            ...item,
            recipientValue: memberContact && memberContact.name
          };
        }
      });

      // Filter list by checking whether the value of recipientValue is falsy.
      const list = recipientList.filter(item => !!item.recipientValue);

      return list;
    },

    isCustomRecipientInputShown() {
      return (
        this.actionObj.channelType === ACTION_CHANNEL_TYPE.EMAIL &&
        this.actionObj.recipientType !== EMAIL_RECIPIENT_TYPE.MYSELF
      );
    },

    isAddButtonDisabled() {
      if (this.actionObj.channelType === ACTION_CHANNEL_TYPE.EMAIL) {
        if (
          this.actionObj.recipientType ===
            EMAIL_RECIPIENT_TYPE.MEMBER_CONTACT &&
          !this.memberContact
        )
          return true;

        if (
          this.actionObj.recipientType ===
            EMAIL_RECIPIENT_TYPE.PROVIDED_ADDRESS &&
          !this.emailAddress
        ) {
          // this.$refs.email.reportValidity();
          return true;
        }
      } else if (this.actionObj.channelType === ACTION_CHANNEL_TYPE.SMS) {
        if (!this.countryCode || !this.mobileNumber) return true;

        const fullMobileNumber = '+' + this.countryCode + this.mobileNumber;
        const isValidMobileNumber = fullMobileNumber.match(/^\+\d{2,15}$/);
        if (!isValidMobileNumber) return true;
      } else if (this.actionObj.channelType === ACTION_CHANNEL_TYPE.SLACK) {
        if (!this.slackId) return true;
      }

      return false;
    }
  },

  watch: {
    actionObj: {
      deep: true,
      handler() {
        this.isDuplicatedRecipient = false;
        this.isInvalidEmailAddress = false;
      }
    },
    memberContact() {
      this.isDuplicatedRecipient = false;
    },
    emailAddress() {
      this.isDuplicatedRecipient = false;
      this.isInvalidEmailAddress = false;
    },
    countryCode() {
      this.isDuplicatedRecipient = false;
    },
    mobileNumber() {
      this.isDuplicatedRecipient = false;
    },
    slackId() {
      this.isDuplicatedRecipient = false;
    }
  },

  methods: {
    backToIndexPage() {
      this.$emit('backToIndexPage');
    },

    setChannelType(val) {
      this.actionObj.channelType = val;
    },

    setRecipientType(val) {
      this.actionObj.recipientType = val;
    },

    setMemberContact(val) {
      this.memberContact = val;
    },

    addToList() {
      if (this.actionObj.channelType === ACTION_CHANNEL_TYPE.EMAIL) {
        if (this.actionObj.recipientType === EMAIL_RECIPIENT_TYPE.MYSELF) {
          const { userid } = this.oidcUser;

          const isInRecipientList = this.checkIsInRecipientList(userid);
          if (isInRecipientList) {
            this.isDuplicatedRecipient = true;
            return;
          }

          this.actionObj.recipientList.push({
            channel: ACTION_CHANNEL_TYPE.EMAIL,
            recipientType: RECIPIENT_TYPE_IN_API.APNIC_USER,
            recipientValue: userid
          });
          this.isDuplicatedRecipient = false;
        } else if (
          this.actionObj.recipientType === EMAIL_RECIPIENT_TYPE.MEMBER_CONTACT
        ) {
          const isInRecipientList = this.checkIsInRecipientList(
            this.memberContact
          );
          if (isInRecipientList) {
            this.isDuplicatedRecipient = true;
            return;
          }

          this.actionObj.recipientList.push({
            channel: ACTION_CHANNEL_TYPE.EMAIL,
            recipientType: RECIPIENT_TYPE_IN_API.APNIC_USER,
            recipientValue: this.memberContact
          });
          this.isDuplicatedRecipient = false;
          // this.memberContact = '';
        } else if (
          this.actionObj.recipientType === EMAIL_RECIPIENT_TYPE.PROVIDED_ADDRESS
        ) {
          const isValidEmailAddress =
            this.$refs.email &&
            this.$refs.email.checkValidity() &&
            validateEmailAddressByLength(this.emailAddress);

          if (!isValidEmailAddress) {
            this.isInvalidEmailAddress = true;

            if (!validateEmailAddressByLength(this.emailAddress)) {
              this.invalidEmailWarningText =
                'The local part of email address must be no longer than 64 characters.';
            } else {
              this.invalidEmailWarningText =
                'The provided email address you have added is not valid.';
            }

            return;
          }

          const isInRecipientList = this.checkIsInRecipientList(
            this.emailAddress
          );
          if (isInRecipientList) {
            this.isDuplicatedRecipient = true;
            return;
          }

          this.actionObj.recipientList.push({
            channel: ACTION_CHANNEL_TYPE.EMAIL,
            recipientType: RECIPIENT_TYPE_IN_API.NON_APNIC_USER,
            recipientValue: this.emailAddress
          });
          this.isDuplicatedRecipient = false;
          this.emailAddress = '';
        }
      } else if (this.actionObj.channelType === ACTION_CHANNEL_TYPE.SMS) {
        const isInRecipientList = this.checkIsInRecipientList(
          `+${this.countryCode}${this.mobileNumber}`
        );
        if (isInRecipientList) {
          this.isDuplicatedRecipient = true;
          return;
        }

        this.actionObj.recipientList.push({
          channel: ACTION_CHANNEL_TYPE.SMS,
          recipientType: RECIPIENT_TYPE_IN_API.NON_APNIC_USER,
          recipientValue: `+${this.countryCode}${this.mobileNumber}`
        });
        this.isDuplicatedRecipient = false;
        this.countryCode = null;
        this.mobileNumber = null;
      } else if (this.actionObj.channelType === ACTION_CHANNEL_TYPE.SLACK) {
        const isInRecipientList = this.checkIsInRecipientList(this.slackId);
        if (isInRecipientList) {
          this.isDuplicatedRecipient = true;
          return;
        }

        this.actionObj.recipientList.push({
          channel: ACTION_CHANNEL_TYPE.SLACK,
          recipientType: RECIPIENT_TYPE_IN_API.NON_APNIC_USER,
          recipientValue: this.slackId
        });
        this.isDuplicatedRecipient = false;
        this.slackId = '';
      }

      this.isAddingActionShown = false;
    },

    checkIsInRecipientList(val) {
      return !!this.actionObj.recipientList.find(
        item => item.recipientValue === val
      );
    },

    removeRecipient(index) {
      this.actionObj.recipientList.splice(index, 1);
    },

    parseChannelType(val) {
      let name = '';
      switch (val) {
        case ACTION_CHANNEL_TYPE.EMAIL:
          name = 'Email';
          break;
        case ACTION_CHANNEL_TYPE.SMS:
          name = 'SMS';
          break;
        case ACTION_CHANNEL_TYPE.SLACK:
          name = 'Slack';
          break;
        default:
          name = 'Unknown';
          break;
      }

      return name;
    },

    preventNonNumericalInput(e) {
      e = e || window.event;

      if (e && e.key && !e.key.match(/[0-9]/)) e.preventDefault();
    },

    handleCountryCodeInput(e) {
      const value = e.target.value;

      if (value && value.length >= 4)
        this.countryCode = Number(value.substring(0, 3));
    },

    handleMobileNumberInput(e) {
      const value = e.target.value;
      if (value && value.length >= 15)
        this.mobileNumber = Number(value.substring(0, 14));
    }
  }
};
</script>

<style lang="scss" scoped>
.action {
  position: relative;
  padding: 2rem 1.5rem;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;

  & > p {
    line-height: 1.5rem;
    margin-bottom: 0;
  }
}

h6 {
  font-weight: 600;

  & > span {
    font-size: 1.2rem;
    padding: 0.5rem 1rem;
    margin-left: -1rem;
    vertical-align: bottom;
    color: $text-light-blue;
  }
}

.separate-line {
  @include separate-line;
}

.action-input-container {
  margin-top: 2rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 80%;
  min-width: 25rem;
  max-width: 60rem;

  .channel {
    width: 6rem;
    margin-right: 0.25rem;
  }

  .recipient {
    flex: 1 1 auto;
    margin-right: 0.25rem;

    .mobile-input-container {
      position: relative;
      display: flex;

      &::before {
        content: '+';
        position: absolute;
        top: 0.4rem;
        left: 0.15rem;
      }

      // number input without arrows
      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* Firefox */
      input[type='number'] {
        -moz-appearance: textfield;
      }

      input::placeholder {
        color: $text-grey;
      }
    }

    .country-code {
      width: 3.5rem;
      margin-right: 0.25rem;
    }
  }

  .add {
    width: 7rem;
    align-self: flex-end;

    button {
      max-width: 7rem;
      min-width: 7rem;
      height: 38px;
    }

    .not-visible {
      visibility: hidden;
    }
  }

  .title {
    min-width: 5rem;
    margin-bottom: 0.25rem;
    width: 100%;

    @include single-line-ellipsis;
  }
}

.custom-recipient-input-container {
  margin-top: 0.5rem;

  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 80%;
  min-width: 25rem;
  max-width: 60rem;

  .custom-recipient-input {
    flex: 1 1 auto;
    margin-right: 0.25rem;
  }

  .add {
    width: 7rem;
    align-self: flex-end;

    button {
      max-width: 7rem;
      min-width: 7rem;
      height: 38px;
    }

    .not-visible {
      visibility: hidden;
    }
  }
}

.help-link {
  margin: 0.5rem 0;

  a {
    color: $text-light-blue;
    cursor: pointer;

    &:hover {
      text-decoration: none;
    }
  }
}

.warning-text {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: $warning-red;
}

.recipient-list-container {
  width: 80%;
  min-width: 25rem;
  max-width: 60rem;
  margin-top: 1rem;
  border: 1px solid #7f7f7f;

  .title-row {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 0 0.5rem;
    height: 2.5rem;
    border-bottom: 1px solid #7f7f7f;

    .title {
      color: #000000;
      font-weight: 600;
    }

    .channel {
      flex: 1 1 25%;
    }

    .recipient {
      flex: 1 1 70%;
    }

    .empty-block {
      flex: 0 0 2rem;
    }
  }

  .list {
    min-height: 2rem;
  }

  ul {
    list-style-type: none;
    margin: 0;

    li {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 0.5rem;

      &:hover {
        background-color: #d9d9d9;
      }

      &:first-of-type {
        padding-top: 0.75rem;
      }

      &:last-of-type {
        padding-bottom: 0.75rem;
      }

      .channel {
        flex: 1 1 25%;
      }

      .recipient {
        flex: 1 1 70%;
      }

      .icon-container {
        flex: 0 0 2rem;
        padding: 0 0.5rem;

        svg {
          color: $icon-grey;
        }

        &:hover {
          cursor: pointer;

          svg {
            color: black;
          }
        }
      }
    }
  }

  .empty-list {
    display: inline-block;
    padding: 0.5rem;
    color: #7f7f7f;
    font-style: italic;
  }
}

.sending-notification {
  margin-top: 1.5rem;
}

.mobile {
  padding: 1rem;

  h6 {
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1rem;
  }

  .empty-recipient {
    .add-action-link {
      margin-top: 2rem;
      color: $text-light-blue;
    }
  }

  .add-recipient-container {
    margin-bottom: 2rem;

    .action-input-container {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      margin-top: 1rem;

      max-width: 100%;
      min-width: 100%;

      .channel,
      .recipient,
      .add {
        width: 100%;
        margin-bottom: 1rem;
      }

      .add {
        text-align: right;

        & > button {
          margin-left: 1rem;
        }
      }

      .title {
        margin-bottom: 0.25rem;
        font-weight: 400;
      }

      .custom-recipient-input {
        margin-top: 1rem;
      }

      .warning-text {
        margin-top: -0.5rem;
        margin-bottom: 0.5rem;
      }

      .help-link {
        margin-top: -0.5rem;
        margin-bottom: 1rem;
      }
    }
  }

  .title {
    font-weight: 600;
    margin-bottom: 1rem;
  }

  .recipient-list-container {
    margin: 0;
    padding: 0;
    width: 100%;
    min-width: 100%;
    border: none;

    .action-title-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;

      .title {
        margin-bottom: 0;
      }
    }

    ul {
      list-style: none;
      margin: 0;

      li.recipient-list {
        padding: 0;
        margin-bottom: 1rem;

        .card {
          width: 100%;
          margin-bottom: 0;

          .card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 1rem;

            &__text {
              font-weight: 600;
            }

            &__icon {
              padding: 0rem;
              font-size: 2rem;
            }
          }

          .card-body {
            padding: 1rem;

            p {
              margin: 0;
            }
          }
        }
      }
    }
  }
}
</style>
